import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Phone, Mail, Calendar, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching quotes:', err);
      setError('Failed to load quote requests');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xl text-slate-600">Loading quote requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-xl text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Quote Requests</h1>
          <p className="text-lg text-slate-600">Total requests: {quotes.length}</p>
        </div>

        {quotes.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <p className="text-slate-600">No quote requests yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {quotes.map((quote) => (
              <Card key={quote.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-2xl text-slate-900">{quote.name}</CardTitle>
                      <div className="flex gap-2 mt-2">
                        <Badge className={quote.formType === 'hero' ? 'bg-cyan-600' : 'bg-blue-600'}>
                          {quote.formType === 'hero' ? 'Hero Form' : 'Contact Page'}
                        </Badge>
                        <Badge variant="outline">
                          {quote.customerType === 'new' ? 'New Customer' : 'Existing Customer'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center text-slate-500">
                      <Calendar className="h-4 w-4 mr-2" />
                      <span className="text-sm">{formatDate(quote.createdAt)}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-slate-700">
                      <Mail className="h-5 w-5 mr-3 text-cyan-600" />
                      <a href={`mailto:${quote.email}`} className="hover:text-cyan-600">
                        {quote.email}
                      </a>
                    </div>
                    <div className="flex items-center text-slate-700">
                      <Phone className="h-5 w-5 mr-3 text-cyan-600" />
                      <a href={`tel:${quote.phone}`} className="hover:text-cyan-600">
                        {quote.phone}
                      </a>
                    </div>
                  </div>
                  
                  {quote.service && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-slate-700 mb-1">Service Requested:</p>
                      <p className="text-slate-900">{quote.service}</p>
                    </div>
                  )}
                  
                  {quote.message && (
                    <div className="bg-slate-50 p-4 rounded-lg">
                      <div className="flex items-start">
                        <MessageSquare className="h-5 w-5 mr-3 text-cyan-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-slate-700 mb-1">Message:</p>
                          <p className="text-slate-900 whitespace-pre-wrap">{quote.message}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuotes;
